<script setup lang="ts">
import useVuelidate from '@vuelidate/core'
import { required, email, maxLength } from '@vuelidate/validators'
import get from 'lodash/get'
import { publicConfig } from '~/config/public'
import { ContactForm, ContactFormSubject } from '~/lib/models/contact'
import { ToastType } from '~/lib/models/ui/toast'
import { GTMFormNameType } from '~/lib/models/gtm'
import { BlockContactForm } from '~/lib/services/content/content.blocks.dtos'
import { contentService } from '~/lib/services/content/content.service'

const props = defineProps<{ block: BlockContactForm }>()

const { t } = useLocale()
const { $toast } = useNuxtApp()
const { trackFormSubmit } = useGTM()
const appStore = useAppStore()

const defaultInput = (): ContactForm => ({
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  city: '',
  subject: '',
  // other conditional
  local: '',
  date: '',
  time: '',
  // more required
  message: '',
  newsletterAccepted: false,
})

const input: ContactForm = reactive(defaultInput())
const fetching = ref(false)
const token = ref('')
const turnstile = ref()
const formName = GTMFormNameType.CONTACT

/* TODO - Validar inputs length? */
const vValidation = useVuelidate(
  {
    firstname: {
      required,
      maxLength: maxLength(40),
    },
    lastname: {
      required,
      maxLength: maxLength(40),
    },
    email: {
      required,
      email,
      maxLength: maxLength(80),
    },
    phone: {
      required,
      maxLength: maxLength(30),
    },
    city: {
      maxLength: maxLength(80),
    },
    subject: {
      required,
      maxLength: maxLength(80),
    },
    local: {
      maxLength: maxLength(80),
    },
    date: {
      maxLength: maxLength(30),
    },
    time: {
      maxLength: maxLength(30),
    },
    message: {
      required,
      maxLength: maxLength(5000),
    },
    newsletterAccepted: {
      required,
    },
  },
  input as unknown as any
)

const maskSufix = computed(() => {
  return appStore.phoneValidationData?.mask || undefined
})

const subjectOptions = computed(() => {
  const options: { key: string; name: string }[] = []
  for (const key of Object.keys(ContactFormSubject)) {
    const keyValue = get(ContactFormSubject, key)
    options.push({
      key: keyValue,
      name: t(`contact.inputs.subject.option.${keyValue}`),
    })
  }
  return options
})

const isClaimSelected = computed(() => {
  if (!input.subject) return false
  return input.subject === ContactFormSubject.CLAIM
})

const siteKey = computed(() => {
  const domain = useSiteDomain()
  const secondaryDomains = (publicConfig.turnstile.secondaryDomains as string[]) || []
  // Check if the current domain is in the secondary domains list
  const isSecondaryDomain = secondaryDomains.includes(domain)
  return isSecondaryDomain
    ? publicConfig.turnstile.secondarySiteKey
    : publicConfig.turnstile.primarySiteKey
})

const resetInput = () => {
  vValidation.value.$reset()

  input.firstname = ''
  input.lastname = ''
  input.email = ''
  input.phone = ''
  input.city = ''
  input.subject = ''
  input.local = ''
  input.date = ''
  input.time = ''
  input.message = ''
  input.newsletterAccepted = false
}

const submit = async () => {
  try {
    const valid = await vValidation.value.$validate()
    if (!valid) return
    fetching.value = true
    trackFormSubmit(formName)

    if (!isClaimSelected) {
      input.local = ''
      input.date = ''
      input.time = ''
    }
    const translatedSubject = t(`contact.inputs.subject.option.${input.subject}`)
    input.subject = translatedSubject

    await contentService.sendContactForm(props.block.id, input, token.value)

    // reset input
    resetInput()
    // success toast
    $toast.show({
      content: t('contact.toast.success'),
      type: ToastType.SUCCESS,
    })
  } catch (error) {
    // error toast
    turnstile.value.reset()
    $toast.show({
      content: t('contact.toast.error'),
      type: ToastType.ERROR,
    })
  } finally {
    fetching.value = false
  }
}
</script>

<template>
  <section class="mx-auto my-10 w-full p-4 sm:max-w-[70%] md:max-w-[43rem]">
    <h2 class="text-2xl font-bold">{{ t('contact.title') }}</h2>

    <!-- Inputs -->
    <div class="my-4">
      <McForm :v="vValidation" :form-name="formName">
        <McInput
          v-model="input.firstname"
          type="text"
          :label="`*${t('contact.inputs.firstname')}`"
          :placeholder="`*${t('contact.inputs.firstname')}`"
          name="firstname"
          class="my-10"
        />

        <McInput
          v-model="input.lastname"
          type="text"
          :label="`*${t('contact.inputs.lastname')}`"
          :placeholder="`*${t('contact.inputs.lastname')}`"
          name="lastname"
          class="my-10"
        />

        <McInput
          v-model="input.email"
          type="text"
          :label="`*${t('contact.inputs.email')}`"
          :placeholder="`*${t('contact.inputs.email')}`"
          name="email"
          class="my-10"
        />

        <McInput
          v-model="input.phone"
          type="text"
          :label="`*${t('contact.inputs.phone')}`"
          :placeholder="`*${t('contact.inputs.phone')}`"
          name="phone"
          class="my-10"
          :mask="maskSufix"
        />

        <McInput
          v-model="input.city"
          type="text"
          :label="t('contact.inputs.city')"
          :placeholder="t('contact.inputs.city')"
          name="city"
          class="my-10"
        />

        <!-- Subject -->
        <McDropdown
          v-model="input.subject"
          :options="subjectOptions"
          :option-value="'key'"
          :option-label="'name'"
          name="subject"
          :label="`*${t('contact.inputs.subject.label')}`"
          :placeholder="t('contact.inputs.subject.placeholder')"
          class="my-10"
        />

        <!-- Only visible when claim is selected as subject -->
        <template v-if="isClaimSelected">
          <McInput
            v-model="input.local"
            type="text"
            :label="t('contact.inputs.local')"
            :placeholder="t('contact.inputs.local')"
            name="local"
            class="my-10"
          />

          <McInput
            v-model="input.date"
            type="text"
            :label="t('contact.inputs.date')"
            :placeholder="t('contact.inputs.date')"
            name="date"
            class="my-10"
          />

          <McInput
            v-model="input.time"
            type="text"
            :label="t('contact.inputs.time')"
            :placeholder="t('contact.inputs.time')"
            name="time"
            class="my-10"
          />
        </template>

        <!-- Message (textarea) -->
        <McTextArea
          v-model="input.message"
          :label="`*${t('contact.inputs.message')}`"
          :placeholder="`*${t('contact.inputs.message')}`"
          name="message"
          class="my-10"
        />
      </McForm>
    </div>

    <!-- Checkbox newsletter  -->

    <div class="my-12 flex w-full flex-col items-center gap-10 md:items-start">
      <NuxtTurnstile ref="turnstile" v-model="token" :site-key="siteKey" />
      <div class="flex flex-row items-center gap-4">
        <div>
          <Checkbox
            v-model:checked="input.newsletterAccepted"
            name="newsletterAccepted"
            @update:checked="(checked: boolean) => input.newsletterAccepted = checked"
          />
        </div>

        <p class="text-xs text-mcd-secondaryDarkGrey">
          {{ t('contact.inputs.newsletterAccepted') }}
        </p>
      </div>
    </div>

    <!-- Action send -->
    <div class="my-6 flex flex-row justify-end md:justify-start">
      <McdButton is-primary class="px-14 py-3" :disabled="fetching" @on-click="submit">
        {{ t('contact.action.save') }}
      </McdButton>
    </div>
  </section>
</template>
